<template>
  <form class="needs-validation ml-0" @submit.prevent="completeForm" novalidate>
    <div class="col px-0 pr-lg-3">
      <section class="bg-white rounded mb-4 p-3 p-sm-4" v-for="(memberDetail, index) in horsePages" :key="index">
        <div class="d-flex justify-content-between align-items-start" v-if="index === 0">
          <h1 class="h2 mb-3">
            {{ edit ? 'Edit' : register ? 'Register' : 'Add' }} Horse<span class="d-none d-sm-inline">s</span>
          </h1>
          <button
            class="btn btn btn-primary d-inline-block d-lg-none"
            @click.prevent="addAnotherHorse()"
          >
            Add<span class="d-none d-sm-inline"> Another</span>
          </button>
        </div>
        <div role="alert" class="alert alert-warning" v-if="errors[index]">{{ errors[index] }}</div>
        <div role="alert" class="alert alert-warning" v-if="commonError">{{ commonError }}</div>
        <div class="row mb-3" v-if="(userRole !== 'Admin' && userRole !== 'SuperAdmin') && userRole !== 'SecretaryOnly'">
          <div class="col-12 col-md-6 form-group">
            <label for="horseName">Horse Name</label>
            <input
              type="text"
              class="form-control"
              id="horseName"
              v-model="horseNames[index]"
              maxlength="40"
              required
              @input="setHorseName(index)"
            >
            <div class="invalid-tooltip">Please include horse Name.</div>
          </div>
          <VueSlideToggle :open="nameMatches[index]" tag="div" :duration="500" class="col-12 px-lg-3">
            <div v-if="matchingNames[index] && (matchingNames[index].length || Object.keys(matchingNames[index]).length)">
              <p>
                Horses similar to this one already exist in the system. Please, note that you do not need to add a new
                horse in case you see it on the list. Link the horse to your account in order to nominate with it.
              </p>
              <table class="table table-striped">
                <thead>
                  <tr>
                    <td>Horse name</td>
                    <td>Owner Name</td>
                    <td>Owner's Member Number</td>
                    <td>Action</td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(horse) in matchingNames[index]" :key="horse.id" class="clickable-row">
                    <td>{{ horse.name }}</td>
                    <td>{{ horse.owner_name }}</td>
                    <td>{{ horse.member_number }}</td>
                    <td>
                      <button type="button" class="btn btn-primary mb-3" :disabled="linked(horse.id)" @click="linkMemberHorse(horse.id)">Link</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <button v-if="userRole !== 'Volunteer'" type="button" class="btn btn-primary mb-3" @click="newHorseToggle(index)">I didn't find the right match, I want to create a horse myself</button>
            </div>
            <div v-else-if="horseNames[index] && userRole !== 'Volunteer'">
              <button type="button" class="btn btn-primary mb-3" @click="newHorseToggle(index)">I want to add my own horse</button>
            </div>
          </VueSlideToggle>
        </div>
        <div class="row align-items-start" v-if="newMemberHorse || userIsSecretary">
          <div class="col-12 form-group" v-if="(userRole === 'Admin' || userRole === 'SuperAdmin')">
            <label for="notes">Notes</label>
            <textarea class="form-control" id="notes" v-model="horseDetails[index].notes"></textarea>
          </div>
          <div class="col-12 col-md-6 form-group row no-gutters">
            <label :for="'horseName' + index">Horse Name</label>
            <input
              type="text"
              class="form-control"
              :id="'horseName' + index"
              required
              v-model="horseDetails[index].name"
            >
            <div class="invalid-tooltip">Please include the Horse Name.</div>
          </div>
          <div class="col-12 col-md-6 form-group mb-md-1">
            <label :for="'horseOwner' + index">Owner</label>
            <div class="not-required">
              <member-search-select
                :element-id="'horseOwner' + index"
                context="owner"
                :value="horseDetails[index].owners || []"
                @input="v => setHorseOwners(index, v)"
              />
              <div class="invalid-tooltip" :style="isOwnerInvalid(index) ? 'display: block': ''">Please include the Owner of the horse.</div>
            </div>
            <small>Maximum of one owner. This must be an ACA member</small>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label :for="'horseDob' + index">Date of Birth</label>
            <input type="date" :class="['form-control form-date', errorStatus === 'dob' && errors[index] ? 'is-invalid' : '']" :id="'horseDob' + index" v-model="horseDetails[index].dob" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
            <div class="invalid-tooltip">Please include the Date of Birth of the horse{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label for="horseSex">Sex</label>
            <select v-model="horseDetails[index].gender" required name="horseSex" id="horseSex" class="form-control">
              <option default></option>
              <option value="Mare">Mare</option>
              <option value="Gelding">Gelding</option>
              <option value="Stallion">Stallion</option>
            </select>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label for="horseSire">Sire <small>(optional)</small></label>
            <div class="not-required">
              <horse-search-select
                :element-id="'horseSire' + index"
                :value="horseDetails[index].sire_id || []"
                @input="v => setHorseParent(index, 'sire_id', v)"
              />
            </div>
          </div>

          <div class="col-12 col-md-6 form-group">
            <label for="horseDam">Dam <small>(optional)</small></label>
            <div class="not-required">
              <horse-search-select
                :element-id="'horseDam' + index"
                :value="horseDetails[index].dam_id || []"
                @input="v => setHorseParent(index, 'dam_id', v)"
              />
            </div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label for="ashs"><abbr title="Australian Stock Horse Number">ASHS#</abbr> <small>(optional)</small></label>
            <input type="text" :class="['form-control', errorStatus === 'ashs' && errors[index] ? 'is-invalid' : '']" id="ashs" v-model="horseDetails[index].ashs_number" maxlength="15">
            <div class="invalid-tooltip">Please include a different ASHS#. This one is taken.</div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <!-- AQHA# is AQHS# -->
            <label for="aqhs"><abbr title="Australian Quarter Horse Number">AQHA#</abbr> <small>(optional)</small></label>
            <input type="text" :class="['form-control', errorStatus === 'aqhs' && errors[index] ? 'is-invalid' : '']" id="aqhs" v-model="horseDetails[index].aqhs_number" maxlength="15">
            <div class="invalid-tooltip">Please include a different AQHA#. This one is taken.</div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label :for="'ownerDisplayName' + index">Horse Owner Display Name <small>(optional)</small></label>
            <input type="text" class="form-control" :id="'ownerDisplayName' + index" v-model="horseDetails[index].owner_display_name">
            <small>This is how the horse owner will be displayed on the draws and the leaderboard</small>
          </div>
          <div class="col-12 col-md-6 form-group mb-md-1">
            <label :for="'horseMember' + index">Rider/s</label>
            <member-search-select
              :element-id="'horseMember' + index"
              context="owner"
              multiple
              :limit="10"
              :value="horseDetails[index].user_id || []"
              @input="v => setHorseMembers(index, v)"
            />
            <div class="invalid-tooltip" :style="isMemberInvalid(index) ? 'display: block': ''">Please include the Rider/s.</div>
          </div>
          <div class="col-12 col-md-6 mb-3 form-group">
            <label for="status">Horse Status</label>
            <select class="form-control" id="status" required v-model="horseDetails[index].horse_status_id">
              <option v-for="(status, index) in $store.state.horseStatus" :key="index" :value="status.id">{{status.name}}</option>
            </select>
            <div class="invalid-tooltip">Please include the Status.</div>
          </div>
          <div class="col-12 col-md-6 form-group">
            <label :for="'horseBoyStatus' + index">Beginning of Year Horse Status</label>
            <select class="form-control" id="horseBoyStatus" required v-model="horseDetails[index].boy_status_id">
              <option v-for="(status, index) in $store.state.horseStatus" :key="index" :value="status.id">{{status.name}}</option>
            </select>
            <div class="invalid-tooltip">Please include the Beginning of Year Status.</div>
          </div>

          <div class="col-12 col-md-6 form-group">
            <label for="proofOfAge">Proof of Age</label>
            <div class="custom-file">
              <input type="file" :class="['custom-file-input font-weight-normal', { 'is-invalid': errorStatus === 'file' }]" id="proofOfAge" accept="image/*, application/pdf" @change="fileUpload($event, index)">
              <label class="custom-file-label font-weight-normal" for="proofOfAge">{{horseDetails[index].file_name ? horseDetails[index].file_name : 'Choose file...'}}</label>
              <small>jpg, jpeg, png, tiff, gif, pdf file type required.</small>
              <button
                v-if="horseDetails[index].file_name"
                type="button"
                class="btn btn-link px-2 pl-md-0 px-xl-2 pr-md-1"
                @click="deleteAttachment(index)"
              >
                <span class="sr-only">Delete File</span>
                <div class="btn-remove">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" aria-hidden="true" viewBox="0 0 17 17"><g fill="currentColor"><path d="M8.5 17C3.8 17 0 13.2 0 8.5S3.8 0 8.5 0 17 3.8 17 8.5 13.2 17 8.5 17zm0-16C4.4 1 1 4.4 1 8.5S4.4 16 8.5 16 16 12.6 16 8.5 12.6 1 8.5 1z"/><path d="M10.6 11.1c-.1 0-.3 0-.4-.1L6 6.8c-.2-.2-.2-.5 0-.7s.5-.2.7 0l4.2 4.2c.2.2.2.5 0 .7-.1 0-.2.1-.3.1z"/><path d="M6.4 11.1c-.1 0-.3 0-.4-.1-.2-.2-.2-.5 0-.7l4.2-4.2c.2-.2.5-.2.7 0s.2.5 0 .7L6.7 11s-.2.1-.3.1z"/></g></svg>
                </div>
              </button>
            </div>
          </div>
          <div class="col-12 col-md-6 form-group"  v-if="(userRole === 'Admin' || userRole === 'SuperAdmin')">
            <div class="row">
              <div class="col-12 col-md-6 form-group">
                <label for="horseAchPoints">Historic ACH points:</label>
                <input @input="validatePointAch($event, index)" type="text" :class="['form-control']" id="horseAchPoints" v-model="horseDetails[index].historic_point_ach"  min="0.00" max="1000.00">
              </div>
              <div class="col-12 col-md-6 form-group">
                <label for="horseRomPoints">Historic ROM points:</label>
                <input @input="validatePointRom($event, index)" type="text" :class="['form-control']" id="horseRomPoints" v-model="horseDetails[index].historic_point_rom"  min="0.00" max="1000.00">
              </div>
            </div>
          </div>
        </div>
        <div class="row align-items-start" v-if="(userRole === 'Admin' || userRole === 'SuperAdmin')">
          <div class="col-12 col-md-6 form-group">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" class="custom-control-input" :id="'horseRegistered' + index" v-model="horseDetails[index].registered" @change="toggleRegisteredDate(index)">
              <label class="custom-control-label" :for="'horseRegistered' + index">Registered</label>
            </div>
          </div>
          <div v-if="horseDetails[index].registered" class="col-12 col-md-6 form-group">
            <label :for="'horseRegisteredDate' + index">Registered Date</label>
            <input type="date" :class="['form-control form-date', errorStatus === 'registration_date' && errors[index] ? 'is-invalid' : '']" :id="'horseRegisteredDate' + index" required v-model="horseDetails[index].registration_date" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
            <div class="invalid-tooltip">Please include the Registered Date of the horse{{ isLegacy() ? ' in the format yyyy-mm-dd' : ''}}.</div>
          </div>
        </div>

        <div class="col-12 d-flex justify-content-end">
          <button class="btn btn-block-mob btn-outline-primary px-lg-5" @click.prevent="horseDetails.splice(index,1);horsePages--" v-if="horsePages > 1">Remove</button>
        </div>
      </section>
    </div>

    <desktop-action-bar v-if="userRole !== 'Volunteer'">
      <button
        v-if="!register"
        class="btn btn-block btn-primary px-4 mb-4"
        @click.prevent="addAnotherHorse()">
        Add Another
      </button>
      <div>
        <h2 class="h4 mb-4">{{ register ? 'Cart' : 'My Horses' }}</h2>
        <h3 class="h6 mb-4">Horses {{ register ? 'ready for registration' : 'being added to list' }}:</h3>
        <table class="table table-borderless">
          <tbody>
            <tr v-for="(horse, horseIndex) in horseDetails" :key="horseIndex">
              <td class="pl-0 pt-0">{{horse.name}}</td>
            </tr>
          </tbody>
        </table>
          <button :class="['btn btn-block btn-primary', register ? 'mb-2' : '']" type="submit" @click="handleSubmit">{{register ? 'Check Out' : 'Add horse'}}</button>
          <div class="d-flex justify-content-between align-items-center" v-if="register">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62.4 39.6" width="25" height="16" aria-labelledby="mastercard"><title id="mastercard">Mastercard</title><circle cx="19.5" cy="19.7" r="19" fill="#ee0005"/><circle cx="43" cy="19.7" r="19" fill="#f9a000"/><path fill="#ff6300" d="M24.1 19.7a19 19 0 0 0 7.2 14.9c4.4-3.5 7.2-8.8 7.2-14.9S35.6 8.3 31.3 4.8a19 19 0 0 0-7.2 14.9z"/></svg>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 505 153" width="35" height="11" aria-labelledby="visa"><title id="visa">Visa</title><rect width="100%" height="100%" fill="none"/><g class="currentLayer"><path fill="#3c58bf" d="M185.3 149L210.9 5h40l-24.8 144z"/><path fill="#293688" d="M185.3 149L218.1 5h32.8l-24.8 144z"/><path fill="#3c58bf" d="M371 6.6C363 3.4 350 .2 334 .2c-40 0-68.8 20-68.8 48.8 0 21.6 20 32.8 36 40s20.8 12 20.8 18.4c0 9.6-12.8 14.4-24 14.4a87 87 0 0 1-38.4-8l-5.6-2.4-5.6 32.8c9.6 4 27.2 8 45.6 8 42.4 0 70.4-20 70.4-50.4 0-16.8-10.4-29.6-34.4-40-14.4-7.2-23.2-11.2-23.2-18.4 0-6.4 7.2-12.8 23.2-12.8 13.6 0 23.2 2.4 30.4 5.6l4 1.6L371 6.6z"/><path fill="#293688" d="M371 6.6C363 3.4 350 .2 334 .2c-40 0-61.6 20-61.6 48.8 0 21.6 12.8 32.8 28.8 40s20.8 12 20.8 18.4c0 9.6-12.8 14.4-24 14.4a87 87 0 0 1-38.4-8l-5.6-2.4-5.6 32.8c9.6 4 27.2 8 45.6 8 42.4 0 70.4-20 70.4-50.4 0-16.8-10.4-29.6-34.4-40-14.4-7.2-23.2-11.2-23.2-18.4 0-6.4 7.2-12.8 23.2-12.8 13.6 0 23.2 2.4 30.4 5.6l4 1.6L371 6.6z"/><path fill="#3c58bf" d="M439.7 5c-9.6 0-16.8.8-20.8 10.4L359 149h43.2l8-24h51.2l4.8 24h38.4L471 5h-31.2zm-18.4 96c2.4-7.2 16-42.4 16-42.4s3.2-8.8 5.6-14.4l2.4 13.6s8 36 9.6 44h-33.6v-.8z"/><path fill="#293688" d="M449.3 5c-9.6 0-16.8.8-20.8 10.4L359 149h43.2l8-24h51.2l4.8 24h38.4L471 5h-21.6zm-28 96c3.2-8 16-42.4 16-42.4s3.2-8.8 5.6-14.4l2.4 13.6s8 36 9.6 44h-33.6v-.8z"/><path fill="#3c58bf" d="M111.7 105.8l-4-20.8c-7.2-24-30.4-50.4-56-63.2l36 128H131l64.8-144h-43.2l-40.8 100z"/><path fill="#293688" d="M111.7 105.8l-4-20.8c-7.2-24-30.4-50.4-56-63.2l36 128H131l64.8-144h-35.2l-48.8 100z"/><path fill="#ffbc00" d="M.5 5l7.2 1.6C59 18.6 94.1 49 107.7 85L93.3 17C91 7.4 83.7 5 75 5H.5z"/><path fill="#f7981d" d="M.5 5c51.2 12 93.6 43.2 107.2 79.2L94.1 27.4A19.5 19.5 0 0 0 75 12.2L.5 5z"/><path fill="#ed7c00" d="M.5 5c51.2 12 93.6 43.2 107.2 79.2L98.1 53c-2.4-9.6-5.6-19.2-16.8-23.2L.5 5z"/><g fill="#051244"><path d="M151.7 101l-27.2-27.2-12.8 30.4-3.2-20c-7.2-24-30.4-50.4-56-63.2l36 128h43.2l20-48zM226.1 149l-34.4-35.2-6.4 35.2zM318.1 99.4c3.2 3.2 4.8 5.6 4 8.8 0 9.6-12.8 14.4-24 14.4a87 87 0 0 1-38.4-8l-5.6-2.4-5.6 32.8c9.6 4 27.2 8 45.6 8 25.6 0 46.4-7.2 58.4-20l-34.4-33.6zM364.5 149h37.6l8-24h51.2l4.8 24h38.4L491 90.6l-48-46.4 2.4 12.8s8 36 9.6 44h-33.6c3.2-8 16-42.4 16-42.4s3.2-8.8 5.6-14.4"/></g></g></svg>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 12" width="26" height="18" aria-labelledby="claimoucher"><title id="claimvoucher">Claim Voucher</title><rect width="100%" height="100%" fill="none"/><g class="currentLayer"><g><path fill="#231815" d="M8 6.5H3.5c-.3 0-.5-.2-.5-.5s.2-.5.5-.5H8c.3 0 .5.2.5.5s-.2.5-.5.5zM8 4H3.5c-.3 0-.5-.2-.5-.5s.2-.5.5-.5H8c.3 0 .5.2.5.5S8.3 4 8 4z"/><path fill="#231815" d="M15.5 12h-14C.7 12 0 11.3 0 10.5v-9C0 .7.7 0 1.5 0h14c.8 0 1.5.7 1.5 1.5v9c0 .8-.7 1.5-1.5 1.5zM1.5 1c-.3 0-.5.2-.5.5v9c0 .3.2.5.5.5h14c.3 0 .5-.2.5-.5v-9c0-.3-.2-.5-.5-.5h-14z"/><path fill="#231815" d="M13.5 9h-10c-.3 0-.5-.2-.5-.5s.2-.5.5-.5h10c.3 0 .5.2.5.5s-.2.5-.5.5z"/><g><path fill="#231815" d="M13.5 6.8h-2.6c-.3 0-.5-.2-.5-.5v-3c0-.3.2-.5.5-.5h2.6c.3 0 .5.2.5.5v3c0 .3-.2.5-.5.5zm-2.1-1H13v-2h-1.6v2z"/></g></g></g></svg>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 17" width="22" height="27" aria-labelledby="banktransfer"><title id="banktransfer">Bank Transfer</title><rect width="100%" height="100%" fill="none"/><g class="currentLayer"><g><path fill="#231815" d="M11.5 17h-9A2.5 2.5 0 0 1 0 14.5v-12C0 1.1 1.1 0 2.5 0h9C12.9 0 14 1.1 14 2.5v12c0 1.4-1.1 2.5-2.5 2.5zm-9-16C1.7 1 1 1.7 1 2.5v12c0 .8.7 1.5 1.5 1.5h9c.8 0 1.5-.7 1.5-1.5v-12c0-.8-.7-1.5-1.5-1.5h-9z"/><path fill="#231815" d="M11.5 13h-9c-.3 0-.5-.2-.5-.5v-10c0-.3.2-.5.5-.5h9c.3 0 .5.2.5.5v10c0 .3-.2.5-.5.5zM3 12h8V3H3v9z"/><g><path fill="#231815" d="M7 15.6a1 1 0 0 1-.7-.3 1 1 0 0 1-.3-.7c0-.3.1-.5.3-.7a1 1 0 0 1 1.4 0c.2.2.3.4.3.7 0 .3-.1.5-.3.7a1 1 0 0 1-.7.3z"/></g></g></g></svg>
          </div>
        </div>
    </desktop-action-bar>

    <button class="d-lg-none btn btn-block btn-primary mb-2" type="submit" @click="handleSubmit">Add horse</button>
  </form>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import HANDLESUBMIT from '@/mixins/formValidation.js'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'
import { VueSlideToggle } from 'vue-slide-toggle'
import DesktopActionBar from '@/components/DesktopActionBar'
import HorseSearchSelect from '@/components/Horses/HorseSearchSelect'
import MemberSearchSelect from '@/components/Members/MemberSearchSelect'

export default {
  name: 'HorseAddDetails',
  props: ['edit', 'register', 'horseRegistrants'],
  mixins: [HANDLESUBMIT, LEGACYBROWSERS],
  components: {
    'horse-search-select': HorseSearchSelect,
    'member-search-select': MemberSearchSelect,
    VueSlideToggle,
    DesktopActionBar
  },
  data() {
    return {
      horsePages: 1,
      horseDetails: [{ gender: 'Mare', notes: '', file_name: '' }],
      horseDetailsHasData: false,
      horseList: {},
      horseListPrice: 36.30,
      errorStatus: '',
      errors: [],
      commonError: '',
      userRole: localStorage.role,
      horseInput: {
        name: '',
        index: 0
      },
      horseNames: [''],
      matchingNames: [],
      nameMatches: [],
      newMemberHorse: false,
      linkedHorses: [],
      userIsSecretary: false,
      files: []
    }
  },
  watch: {
    horseNames: {
      handler() {
        this.getNameMatch()
      },
      deep: true
    }
  },
  created() {
    this.getNameMatch = _.debounce(() => {
      this.matchName()
    }, 300)
  },
  computed: {},
  methods: {
    // sire_id / dam_id are submitted as [id] (HorseController reads `[0]`); keep that shape.
    setHorseParent(index, key, value) {
      this.$set(this.horseDetails[index], key, Array.isArray(value) ? value : [])
    },
    // owners / user_id are submitted as-is to /horse/add (array of id strings) — same shape the
    // legacy tags-input v-model produced.
    setHorseOwners(index, value) {
      this.$set(this.horseDetails[index], 'owners', value)
    },
    setHorseMembers(index, value) {
      this.$set(this.horseDetails[index], 'user_id', value)
    },
    isOwnerInvalid(index) {
      if (this.wasValidated) {
        return !this.horseDetails[index].owners || this.horseDetails[index].owners.length === 0
      }
      return false
    },
    isMemberInvalid(index) {
      if (this.wasValidated) {
        if (this.horseDetails[index].user_id === undefined) {
          return true
        } else if (this.horseDetails[index].user_id.length === 0) {
          return true
        }
      }
      return false
    },
    completeForm() {
      setTimeout(() => {
        if (this.formComplete) {
          if (this.register) {
            this.checkout()
          } else {
            this.addHorses()
          }
        }
      }, 10)
    },
    addHorses() {
      const formData = new FormData()
      for (let i = 0; i < this.files.length; i++) {
        const file = this.files[i]
        if (this.files[i] && this.files[i].size > 1024 * 1024) {
          this.errorStatus = 'file'
          this.errors[i] = `The Proof of Age file may not be greater than 1 mb.`
        }
        formData.append(`files[${i}]`, file)
      }
      formData.append('fields', JSON.stringify({ horses: this.horseDetails, id: localStorage.userID }))

      axios.post(`${this.$api}/horse/add`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
        .then((response) => {
          this.errors = []
          this.commonError = ''
          // eslint-disable-next-line
          // console.log(response)
          if (response.data.status === 'success') {
            let horseName = this.horseDetails.reduce((previousValue, currentValue) => previousValue.concat(currentValue.name, ', '), '')
            horseName = horseName.slice(0, -2)
            this.$router.push({
              path: this.$route.name === 'Add Horses' ? '/admin-horses' : this.$route.name === 'Secretary Add Horses' ? '/secretary-horses' : '/horses',
              query: { success: `${horseName} ${this.horseDetails.length > 1 ? 'have' : 'has'}  been added successfully.` }
            })
          } else if (response.data.errors) {
            if (Object.keys(response.data.errors)
              .filter(k => k.includes("dob")).length > 0) {
              // DoB
              this.errorStatus = 'dob'
              this.errors[Object.keys(response.data.errors)
                .filter(k => k.includes("dob"))[0].match(/\d+/)[0]] = `Please include the Date of Birth in the format ${this.isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy'}.`
            } else if (Object.keys(response.data.errors)
              .filter(k => k.includes("ashs")).length > 0) {
              this.errorStatus = 'ashs'
              this.errors[Object.keys(response.data.errors)
                .filter(k => k.includes("ashs"))[0].match(/\d+/)[0]] = `Please include a different ASHS#. This one is taken.`
            } else if (Object.keys(response.data.errors)
              .filter(k => k.includes("aqhs")).length > 0) {
              this.errorStatus = 'aqhs'
              this.errors[Object.keys(response.data.errors)
                .filter(k => k.includes("aqhs"))[0].match(/\d+/)[0]] = `Please include a different AQHA#. This one is taken.`
            } else if (Object.keys(response.data.errors)
              .filter(k => k.includes("file")).length > 0) {
              this.errorStatus = 'file'
              const fileKey = Object.keys(response.data.errors).filter(k => k.includes("file"))[0]
              this.errors[fileKey.match(/\d+/)[0]] = Object.values(response.data.errors[fileKey]).flat()[0]
            }
          } else if (response.data.status === 'horse_limit' || response.data.status === 'invalid_data') {
            this.commonError = response.data.message
          } else {
            this.errors.splice(0, 1, Object.entries(response.data.errors)[0][1][0])
          }
        })
        .catch((error) => {
          if (error.response?.status === 413 || !error.response) {
            this.success = ''
            this.errorStatus = 'file'
            this.commonError = `The Proof of Age file may not be greater than 1 mb.`
          }
          this.errors = []
          // eslint-disable-next-line
          console.log(error)
        })
    },
    checkout() {
      setTimeout(() => {
        if (this.formComplete) {
          const horses = {
            primary_id: localStorage.userID,
            horses: this.horseDetails
          }
          this.$store.commit('updateCartMembers', horses)
          this.$router.push({ name: 'Register Horse Cart', params: horses })
        }
      }, 10)
    },
    toggleRegisteredDate(index) {
      if (this.horseDetails[index].registered === false) {
        this.horseDetails[index].registration_date = null
      }
    },
    matchName() {
      if (
        this.horseInput?.name &&
        this.horseInput?.name.length > 0
      ) {
        const index = this.horseInput.index
        axios.post(`${this.$api}/horse-search`, {
          name: this.horseInput.name
        }).then((response) => {
          this.nameMatches.splice(index, 1, true)
          if (response.data.matching.length || typeof response.data.matching === 'object') {
            this.matchingNames.splice(index, 1, response.data.matching)
            if (this.userRole === 'Volunteer' && response.data.matching.length === 0) {
              this.commonError = 'There are no results.'
            } else {
              this.commonError = ''
            }
          } else {
            this.matchingNames[index] = []
          }
        })
      }
    },
    newHorseToggle(index) {
      this.newMemberHorse = true
      this.nameMatches.splice(index, 1, false)
      this.horseDetails[index].name = this.horseNames[index]
      this.horseDetails[index].notes = ''
    },
    linkMemberHorse(horse_id) {
      if (horse_id) {
        axios.post(`${this.$api}/horse/link-member`, {
          horse_id
        }).then((response) => {
          if (response.data.status === 'success') {
            this.linkedHorses.push(horse_id)
            if (this.$route.name === 'Admin Add Horse' && (this.userRole === 'Admin' || this.userRole === 'SuperAdmin')) {
              this.$router.push('/admin-horses')
            } else if (this.$route.name === 'Admin Add Horse' && (this.userRole === 'Secretary' || this.userRole === 'SecretaryOnly')) {
              this.$router.push('/secretary-horses')
            } else {
              this.$router.push('/horses')
            }
          } else if (response.data.status === 'horse_limit') {
            this.commonError = response.data.message
          }
        }
        ).catch(error => console.log(error))
      }
    },
    linked(horse_id) {
      return this.linkedHorses.includes(horse_id)
    },
    fileUpload(event, index) {
      this.errorStatus = ''
      this.horseDetails[index].file_name = ''
      // eslint-disable-next-line
      _.remove(this.errors, (value, ind)=> {
        return ind === index
      })
      const uploadedFile = event.target.files[0]
      this.files[index] = uploadedFile
      if (uploadedFile && this.files[index].size > 1024 * 1024) {
        this.errorStatus = 'file'
        this.errors[index] = `The Proof of Age file may not be greater than 1 mb.`
      }
      if (uploadedFile) {
        this.horseDetails[index].file_name = uploadedFile.name
      }
    },
    deleteAttachment(index) {
      delete this.files[index]
      this.files[index] = undefined
      this.horseDetails[index].file_name = ''
      this.errorStatus = ''
      // eslint-disable-next-line
      _.remove(this.errors, (value, ind)=> {
        return ind === index
      })
    },
    validatePointAch(event, index) {
      this.horseDetails[index].historic_point_ach = event.target.value.replaceAll(/[^0-9.]/g, '')
    },
    validatePointRom(event, index) {
      this.horseDetails[index].historic_point_rom = event.target.value.replaceAll(/[^0-9.]/g, '')
    },
    setHorseName(index) {
      this.horseInput = {
        name: this.horseNames[index],
        index
      }
    },
    addAnotherHorse() {
      if (this.userRole === 'Volunteer') {
        return
      }
      this.horseDetails.push({
        gender: 'Mare',
        notes: '',
        boyStatusDiffers: false,
        file_name: '',
        name: ''
      })
      this.horseNames.push('')
      this.horsePages++
    }
  },
  mounted() {
    /* Check if a user role is Admin or Secretary or Secretary Only */
    if ((this.userRole === 'Admin' || this.userRole === 'SuperAdmin') || this.userRole === 'Secretary' || this.userRole === 'SecretaryOnly') {
      this.userIsSecretary = true
    }
    /* Get horses to register */
    if (this.register && this.horseRegistrants.length > 0) {
      this.horseDetails = []
      this.$store.state.selectedRows.forEach((row) => {
        this.horseDetails.push(row)
      })
      this.horsePages = this.horseRegistrants.length
    }
  }
}
</script>




// WEBPACK FOOTER //
// src/views/Horses/Add/HorseAddDetails.vue